import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "i18next";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
} from "ui";
import { z } from "zod";

import { ExerciseMovementPatternsCombobox } from "src/components/ExerciseMovementPatternsCombobox";

import { ExerciseCategoryCombobox } from "./ExerciseCategoryCombobox";
import {
  ExercisePrimaryMuscleGroupCombobox,
  ExerciseSecondaryMuscleGroupsCombobox,
} from "./ExerciseMuscleSecondaryGroupsCombobox";

const createExerciseSchema = () =>
  z.object({
    name: z.string().min(1, { message: t("requiredField") }),
    description: z.string(),
    videoURL: z.url().optional(),
    category: z.string().min(1, { message: t("requiredField") }),
    primaryMusclesId: z.string().min(1, { message: t("requiredField") }),
    secondaryMusclesIds: z.array(z.string()),
    movementPatternIds: z.array(z.string()),
  });

export type CreateExerciseValues = z.infer<
  ReturnType<typeof createExerciseSchema>
>;

export interface ExerciseFormProps {
  formId?: string;
  onSubmit: (values: CreateExerciseValues) => Promise<void>;
  defaultValues: CreateExerciseValues;
  className?: string;
}

export function ExerciseForm(props: ExerciseFormProps) {
  const form = useForm<CreateExerciseValues>({
    resolver: zodResolver(createExerciseSchema()),
    defaultValues: props.defaultValues,
  });
  const { t } = useTranslation();

  return (
    <Form {...form}>
      <form
        id={props.formId}
        onSubmit={form.handleSubmit(props.onSubmit)}
        className="flex flex-col gap-2"
      >
        <FormField
          control={form.control}
          name="name"
          required
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("createExercise.name.label")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t("createExercise.name.placeholder")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("createExercise.description.label")}</FormLabel>
              <FormControl>
                <Textarea
                  className={"resize-none"}
                  {...field}
                  placeholder={t("createExercise.description.placeholder")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="videoURL"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("createExercise.videoURL.label")}</FormLabel>
              <FormControl>
                <Input
                  className={"resize-none"}
                  {...field}
                  placeholder={t("createExercise.videoURL.placeholder")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex gap-2">
          <FormField
            control={form.control}
            name="category"
            required
            render={({ field }) => {
              return (
                <FormItem className="flex-1">
                  <FormLabel>{t("createExercise.category.label")}</FormLabel>
                  <FormControl className={""}>
                    <ExerciseCategoryCombobox
                      onChange={field.onChange}
                      value={field.value}
                      placeholder={t("createExercise.category.placeholder")}
                      notFoundText={t("createExercise.category.notFound")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="primaryMusclesId"
            required
            render={({ field }) => {
              return (
                <FormItem className="flex-1">
                  <FormLabel>
                    {t("createExercise.primaryMuscles.label")}
                  </FormLabel>
                  <FormControl>
                    <ExercisePrimaryMuscleGroupCombobox
                      onChange={field.onChange}
                      value={field.value}
                      placeholder={t(
                        "createExercise.primaryMuscles.placeholder",
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>
        <FormField
          control={form.control}
          name="secondaryMusclesIds"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>
                  {t("createExercise.secondaryMuscles.label")}
                </FormLabel>
                <ExerciseSecondaryMuscleGroupsCombobox
                  onChange={field.onChange}
                  value={field.value}
                  placeholder={t("createExercise.secondaryMuscles.placeholder")}
                />
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <FormField
          control={form.control}
          name="movementPatternIds"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>
                  {t("createExercise.movementPatterns.label")}
                </FormLabel>
                <ExerciseMovementPatternsCombobox
                  onChange={field.onChange}
                  value={field.value}
                  placeholder={t("createExercise.movementPatterns.placeholder")}
                />
                <FormMessage />
              </FormItem>
            );
          }}
        />
      </form>
    </Form>
  );
}
